import { useState, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useContractorsApi } from '@/services/api';
import type { ContractorListItem } from '@ip/shared-types';

interface ContractorSelectorProps {
  companyNip: string;
  value?: string | null;
  onChange?: (contractorId: string | null, contractor: ContractorListItem | null) => void;
}

const PL_MAP: Record<string, string> = { ą:'a',ć:'c',ę:'e',ł:'l',ń:'n',ó:'o',ś:'s',ź:'z',ż:'z' };

function normalizeSearch(text: string): string {
  return text.toLowerCase().replace(/[ąćęłńóśźż]/g, c => PL_MAP[c] ?? c).replace(/[^a-z0-9]/g, '');
}

export function ContractorSelector({ companyNip, value, onChange }: ContractorSelectorProps) {
  const [contractors, setContractors] = useState<ContractorListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { getContractors } = useContractorsApi();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getContractors(companyNip)
      .then(data => { if (!cancelled) setContractors(data); })
      .catch(err => { if (!cancelled) console.error('Failed to load contractors:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyNip, getContractors]);

  const selected = useMemo(
    () => contractors.find((c) => c.id === value) ?? null,
    [contractors, value],
  );

  const filtered = useMemo(() => {
    if (!search) return contractors;
    const needle = normalizeSearch(search);
    return contractors.filter(
      (c) =>
        normalizeSearch(c.name || '').includes(needle) ||
        normalizeSearch(c.nip || '').includes(needle),
    );
  }, [contractors, search]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="w-56 justify-between font-normal"
        >
          <span className="truncate">
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              selected?.name || 'Link contractor...'
            )}
          </span>
          <span className="flex items-center gap-0.5">
            {selected && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear contractor"
                className="hover:bg-accent rounded p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.(null, null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onChange?.(null, null);
                  }
                }}
              >
                <X className="size-3" />
              </span>
            )}
            <ChevronsUpDown className="size-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search contractor..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No contractors found</CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.id}
                  onSelect={() => {
                    onChange?.(c.id, c);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('size-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{c.name || 'Unnamed'}</span>
                  {c.nip && (
                    <Badge variant="outline" className="ml-auto font-mono text-[10px]">
                      {c.nip}
                    </Badge>
                  )}
                  {c.country && c.country !== 'PL' && (
                    <Badge variant="outline" className="text-[10px] text-sky-600 dark:text-sky-400">
                      {c.country}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
